import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { Voucher } from 'src/features/vouchers/utils/types';
import UpdateVoucherFormModal from 'src/features/vouchers/components/forms/UpdateVoucherFormModal';
import DeleteVoucherFormModal from 'src/features/vouchers/components/forms/DeleteVoucherFormModal';
import { Link } from 'react-router-dom';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type CellVoucherActions = {
    row: Row<Voucher>;
};

export default function CellVoucherActions({ row }: CellVoucherActions) {
    const { formatMessage } = useIntl();

    const usedAmountGreaterThanZero = row.original.used_amount ? row.original.used_amount > 0 : false;
    const isVoucherUsed = row.original.is_used;
    const permissionManageVouchers = useHasPermissions([PERMISSION.MANAGE_VOUCHERS]);

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Link to={`/vouchers/${row.original.id}`}>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>

                {permissionManageVouchers && !isVoucherUsed && !usedAmountGreaterThanZero && (
                    <>
                        <Tooltip>
                            <UpdateVoucherFormModal
                                semantics={{ entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase() }}
                                selectedVoucher={row.original}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdateVoucherFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeleteVoucherFormModal selectedVoucher={row.original}>
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeleteVoucherFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
            </TooltipProvider>
        </div>
    );
}
